import React, { useState } from "react";
import Layout from "../../Component/Layout";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MASTER_API } from "../../apiConfig";
import { FaClipboardCheck } from "react-icons/fa";

// Single-table update — Inspection Parameters (Inspection Characteristics).
// Click Update -> modal with Download Template + Browse File -> upload excel -> upsert into the table.
const UPDATE_ACTION = {
  key: "inspection-characteristics",
  title: "Update FG Inspection Parameters Table",
  description: "Refresh inspection characteristic standard values via Excel upload.",
  icon: FaClipboardCheck,
};

const TEMPLATE_FILE_NAME = "QA_FG_Inspection_Parameters_Template.xlsx";

// IMPORTANT: change "token" to whatever key your app uses in localStorage
// (check your login page / other data-update pages).
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function InspectionCharacteristicsDataUpdate() {
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  // Download through axios (not window.location.href) so the auth header is sent.
  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${MASTER_API}/inspection-data-update/template/${UPDATE_ACTION.key}`,
        {
          responseType: "blob",
          headers: getAuthHeaders(),
        }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = TEMPLATE_FILE_NAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);

      // When responseType is "blob", error bodies also come back as a Blob — read it as text.
      let message = "Failed to download template";
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          message = JSON.parse(text)?.message || message;
        }
      } catch (_) {
        /* ignore parse errors */
      }
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const res = await axios.post(
        `${MASTER_API}/inspection-data-update/update/${UPDATE_ACTION.key}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getAuthHeaders(),
          },
        }
      );
      toast.success(res.data?.message || "Updated successfully");
      closeModal();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const Icon = UPDATE_ACTION.icon;

  return (
    <Layout>
      <div style={styles.pageTitleBar}>
        <div>
          <div style={styles.pageTitle}>Inspection Parameters Data Update</div>
          <div style={styles.pageSubtitle}>Trigger an update for the Inspection Parameters master table</div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <div style={styles.cardGrid}>
          <div style={styles.actionCard}>
            <div style={styles.actionIconWrap}>
              <Icon size={20} color="#021843" />
            </div>
            <div style={styles.actionTitle}>{UPDATE_ACTION.title}</div>
            <div style={styles.actionDescription}>{UPDATE_ACTION.description}</div>
            <button style={{ ...styles.saveBtn, marginTop: "auto" }} onClick={openModal}>
              Update
            </button>
          </div>
        </div>
      </div>

      {/* ===================== UPLOAD MODAL ===================== */}
      {modalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h4 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>{UPDATE_ACTION.title}</h4>
              <button onClick={closeModal} style={styles.closeBtn}>✖</button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                <button
                  style={{ ...styles.downloadBtn, opacity: downloading ? 0.7 : 1 }}
                  onClick={handleDownloadTemplate}
                  disabled={downloading}
                >
                  {downloading ? "Downloading..." : "Download Template"}
                </button>
              </div>

              <div style={{ textAlign: "center" }}>
                <input
                  type="file"
                  id="inspectionDataUploadInput"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                <label htmlFor="inspectionDataUploadInput" style={styles.browseBtn}>
                  {uploading ? "Uploading..." : "Browse File"}
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnHover />
    </Layout>
  );
}

/* ================= STYLES (matches GTPDataUpdate.js) ================= */
const styles = {
  pageTitleBar:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 },
  pageTitle:      { fontSize: 30, fontWeight: 700, color: "#1e293b", marginBottom: 2 },
  pageSubtitle:   { fontSize: 12, color: "#64748b" },
  tableContainer: { background: "#ffffff", borderRadius: 8, padding: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "18px", padding: "12px" },
  actionCard: {
    background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px",
    boxShadow: "0 2px 8px rgba(15,23,42,0.06)", display: "flex", flexDirection: "column", minHeight: "180px",
    maxWidth: "320px",
  },
  actionIconWrap: {
    width: 40, height: 40, borderRadius: "8px", background: "#eef2ff",
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "12px",
  },
  actionTitle:       { fontSize: "15px", fontWeight: 700, color: "#1e293b", marginBottom: "6px" },
  actionDescription: { fontSize: "12px", color: "#64748b", marginBottom: "16px", lineHeight: 1.5 },
  saveBtn: {
    background: "#021843", color: "#fff", border: "none", borderRadius: "8px",
    padding: "10px 26px", fontSize: "14px", fontWeight: 600, cursor: "pointer", alignSelf: "flex-start",
  },
  modalBackdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1060,
    display: "flex", justifyContent: "center", alignItems: "center",
  },
  modal: { background: "#fff", width: "450px", borderRadius: 12, padding: 25, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  closeBtn: { background: "transparent", border: "none", fontSize: 18, cursor: "pointer", color: "#64748b" },
  modalBody: { background: "#f8fafc", border: "2px dashed #cbd5e1", borderRadius: 8, padding: 24 },
  downloadBtn: { background: "#021843", color: "#fff", padding: "8px 20px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  browseBtn: { background: "#021843", color: "#fff", padding: "10px 24px", borderRadius: 6, cursor: "pointer", fontWeight: 600, display: "inline-block" },
};

export default InspectionCharacteristicsDataUpdate;